<template>
    <component
        :is="tagResolved"
        :type="tag === 'button' ? type : undefined"
        :href="href || undefined"
        :target="tagResolved === 'a' ? (target || undefined) : undefined"
        :rel="tagResolved === 'a' ? (rel || undefined) : undefined"
        :download="tagResolved === 'a' ? download : undefined"
        :disabled="tag === 'button' ? (disabled || loading) : undefined"
        :class="[baseClass, variantClass, sizeClass, blockClass]"
        @click="$emit('click', $event)"
    >
        <svg v-if="loading" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        <Icon v-else-if="iconLeft" :name="iconLeft" :size="iconSize" :tone="iconTone" />
        <span v-if="$slots.default" class="truncate"><slot /></span>
        <Icon v-if="iconRight && !loading" :name="iconRight" :size="iconSize" :tone="iconTone" />
    </component>
</template>

<script>
import { Link } from '@inertiajs/vue3';
import Icon from './Icon.vue';

const VARIANTS = {
    primary: 'bg-gradient-to-br from-violet-cta-light to-violet-cta text-on-primary hover:from-violet-cta hover:to-violet-cta-dark shadow-violet-primary hover:shadow-violet-primary-lg',
    secondary: 'bg-white/60 backdrop-blur-sm text-on-surface border border-white/50 hover:bg-white/80 hover:border-white/70',
    ghost: 'bg-transparent text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface',
    danger: 'bg-error text-on-error hover:bg-error-container hover:text-on-error-container shadow-[0_1px_2px_rgba(0,0,0,0.04)]',
    accent: 'bg-tertiary-container text-on-tertiary-container hover:bg-tertiary hover:text-on-tertiary shadow-[0_4px_16px_rgba(201,167,77,0.20)]',
    inverse: 'bg-inverse-surface text-inverse-on-surface hover:bg-surface-container-highest hover:text-on-surface',
    icon: 'bg-surface text-on-surface-variant hover:bg-surface-container hover:text-on-surface rounded-full p-0 aspect-square',
};

const SIZES = {
    sm: 'h-9 px-3 text-xs',
    md: 'h-11 px-5 text-sm',
    lg: 'h-12 px-6 text-base',
    iconSm: 'w-9 h-9',
    iconMd: 'w-11 h-11',
    iconLg: 'w-12 h-12',
};

export default {
    name: 'UIButton',
    components: { Icon, Link },
    emits: ['click'],
    props: {
        variant: {
            type: String,
            default: 'primary',
            validator: (v) => ['primary', 'secondary', 'ghost', 'danger', 'accent', 'inverse', 'icon'].includes(v),
        },
        size: { type: String, default: 'md' },
        type: { type: String, default: 'button' },
        as: { type: String, default: 'button' },
        href: { type: String, default: '' },
        target: { type: String, default: '' },
        rel: { type: String, default: '' },
        download: { type: [Boolean, String], default: false },
        disabled: { type: Boolean, default: false },
        loading: { type: Boolean, default: false },
        block: { type: Boolean, default: false },
        iconLeft: { type: String, default: '' },
        iconRight: { type: String, default: '' },
    },
    computed: {
        tag() { return this.as; },
        tagResolved() {
            if (this.as !== 'link') return 'button';
            const external = this.target === '_blank'
                || /\.pdf(\?|$)/i.test(this.href)
                || this.download === true || typeof this.download === 'string';
            return external ? 'a' : Link;
        },
        baseClass() {
            return 'inline-flex items-center justify-center gap-2 font-semibold rounded-full transition-all duration-200 ease-spring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.97]';
        },
        variantClass() {
            return VARIANTS[this.variant] || VARIANTS.primary;
        },
        sizeClass() {
            if (this.variant === 'icon') {
                return { sm: SIZES.iconSm, md: SIZES.iconMd, lg: SIZES.iconLg }[this.size];
            }
            return SIZES[this.size] || SIZES.md;
        },
        iconTone() {
            if (this.variant === 'primary' || this.variant === 'danger' || this.variant === 'accent') return 'inverse';
            return 'muted';
        },
        iconSize() { return { sm: 'sm', md: 'sm', lg: 'md' }[this.size] || 'md'; },
        blockClass() { return this.block ? 'w-full' : ''; },
    },
};
</script>
